import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, Calendar, Target, CheckCircle2, XCircle, Minus, Sparkles, TrendingUp } from 'lucide-react';
import { ChallengeCorrelationsPanel } from '@/components/challenges/ChallengeCorrelationsPanel';
import { buildChallengesContextForAI } from '@/utils/buildChallengesContextForAI';
import { format, differenceInDays, parseISO } from 'date-fns';

interface WellnessTemplate {
  id: string;
  title: string;
  short_label: string;
  category: string;
  metric_source: string;
  metric_key: string | null;
  target_type: string;
  default_target_value: number | null;
  unit: string | null;
}

interface WellnessUserChallenge {
  id: string;
  user_id: string;
  custom_title: string | null;
  custom_goal: string | null;
  category: string;
  metric_source: string;
  metric_key: string | null;
  target_type: string;
  target_value: number | null;
  unit: string | null;
  duration_days: number;
  start_date: string;
  end_date: string;
  status: string;
  reason_text: string | null;
  created_at: string;
  template: WellnessTemplate | null;
}

interface ChallengeCheckin {
  id: string;
  checkin_date: string;
  completed: boolean;
  measured_value: number | null;
  notes: string | null;
}

const ChallengeDetail: React.FC = () => {
  const { challengeId } = useParams<{ challengeId: string }>();
  const navigate = useNavigate();
  
  const [challenge, setChallenge] = useState<WellnessUserChallenge | null>(null);
  const [checkins, setCheckins] = useState<ChallengeCheckin[]>([]);
  const [therappiReflection, setTherappiReflection] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [reflectionLoading, setReflectionLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!challengeId) {
        setError('No challenge ID provided');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('Please sign in to view this challenge');
        setLoading(false);
        return;
      }

      // Fetch the challenge
      const { data: challengeData, error: challengeError } = await supabase
        .from('wellness_user_challenges')
        .select(`
          id,
          user_id,
          custom_title,
          custom_goal,
          category,
          metric_source,
          metric_key,
          target_type,
          target_value,
          unit,
          duration_days,
          start_date,
          end_date,
          status,
          reason_text,
          created_at,
          wellness_challenge_templates (
            id,
            title,
            short_label,
            category,
            metric_source,
            metric_key,
            target_type,
            default_target_value,
            unit
          )
        `)
        .eq('id', challengeId)
        .eq('user_id', user.id)
        .single();

      if (challengeError) {
        console.error('Error fetching challenge', challengeError);
        setError('Could not load this challenge. It may not exist or you may not have access.');
        setLoading(false);
        return;
      }

      const template = challengeData.wellness_challenge_templates as WellnessTemplate | null;
      setChallenge({
        ...challengeData,
        template
      } as WellnessUserChallenge);

      // Fetch all checkins for this challenge
      const { data: checkinData, error: checkinError } = await supabase
        .from('wellness_challenge_checkins')
        .select('id, checkin_date, completed, measured_value, notes')
        .eq('user_challenge_id', challengeId)
        .order('checkin_date', { ascending: true });

      if (checkinError) {
        console.error('Error fetching checkins', checkinError);
      } else {
        setCheckins(checkinData || []);
      }

      setLoading(false);

      // Fetch Therappi reflection
      try {
        const reflectionText = await buildChallengesContextForAI(user.id);
        setTherappiReflection(reflectionText);
      } catch (err) {
        console.error('Error building challenge context', err);
      }
      setReflectionLoading(false);
    };

    fetchData();
  }, [challengeId]);

  const calculateStreak = (): number => {
    if (checkins.length === 0) return 0;
    
    // Sort by date descending to find current streak
    const sortedCheckins = [...checkins]
      .filter(c => c.completed)
      .sort((a, b) => new Date(b.checkin_date).getTime() - new Date(a.checkin_date).getTime());
    
    if (sortedCheckins.length === 0) return 0;
    
    let streak = 1;
    for (let i = 0; i < sortedCheckins.length - 1; i++) {
      const current = parseISO(sortedCheckins[i].checkin_date);
      const next = parseISO(sortedCheckins[i + 1].checkin_date);
      const diff = differenceInDays(current, next);
      
      if (diff === 1) {
        streak++;
      } else {
        break;
      }
    }
    
    return streak;
  };

  const getTargetTypeLabel = (targetType: string): string => {
    switch (targetType) {
      case 'at_least': return '≥';
      case 'at_most': return '≤';
      case 'boolean': return 'Yes/No';
      case 'range': return 'Range';
      default: return targetType;
    }
  };

  const formatMetricSource = (source: string): string => {
    return source.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Skeleton className="h-8 w-8" />
          <Skeleton className="h-6 w-48" />
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-64" />
            <Skeleton className="h-4 w-32" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-32 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !challenge) {
    return (
      <div className="min-h-screen bg-background p-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <Card className="border-destructive/30 bg-destructive/5">
          <CardContent className="py-6">
            <p className="text-destructive text-sm">{error || 'Challenge not found'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const title = challenge.custom_title || challenge.template?.short_label || challenge.template?.title || 'Personal Challenge';
  const completedDays = checkins.filter(c => c.completed).length;
  const progressPercent = challenge.duration_days > 0 
    ? Math.round((completedDays / challenge.duration_days) * 100) 
    : 0;
  const streak = calculateStreak();
  const metricSource = challenge.metric_source || challenge.template?.metric_source || '';
  const metricKey = challenge.metric_key || challenge.template?.metric_key || null;
  const targetType = challenge.target_type || challenge.template?.target_type || '';
  const targetValue = challenge.target_value ?? challenge.template?.default_target_value ?? null;
  const unit = challenge.unit || challenge.template?.unit || '';

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto p-4 space-y-4 pb-20">
        {/* Back Button */}
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-2">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {/* A. Challenge Header */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1.5">
                <CardTitle className="text-xl">{title}</CardTitle>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary" className="text-xs capitalize">
                    {challenge.category}
                  </Badge>
                  <Badge 
                    variant={challenge.status === 'active' ? 'default' : 'outline'} 
                    className="text-xs capitalize"
                  >
                    {challenge.status}
                  </Badge>
                </div>
              </div>
              <Target className="h-8 w-8 text-primary/50" />
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4" />
                <span>{challenge.duration_days} days</span>
              </div>
              <span>•</span>
              <span>
                {format(parseISO(challenge.start_date), 'MMM d')} → {format(parseISO(challenge.end_date), 'MMM d, yyyy')}
              </span>
            </div>
            {challenge.reason_text && (
              <p className="text-sm text-muted-foreground italic border-l-2 border-primary/30 pl-3">
                Why this matters: {challenge.reason_text}
              </p>
            )}
          </CardContent>
        </Card>

        {/* B. Progress Section */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              Progress
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 rounded-lg bg-muted/50">
                <p className="text-2xl font-semibold text-primary">{completedDays}</p>
                <p className="text-xs text-muted-foreground">Days Done</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-muted/50">
                <p className="text-2xl font-semibold text-foreground">{progressPercent}%</p>
                <p className="text-xs text-muted-foreground">Complete</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-muted/50">
                <p className="text-2xl font-semibold text-secondary">{streak}</p>
                <p className="text-xs text-muted-foreground">Day Streak</p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{completedDays} / {challenge.duration_days} days</span>
                <span>{progressPercent}%</span>
              </div>
              <Progress value={progressPercent} className="h-2" />
            </div>

            {/* Check-in History */}
            {checkins.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Check-in History</p>
                <div className="flex flex-wrap gap-1.5">
                  {checkins.map((c) => (
                    <div
                      key={c.id}
                      title={`${format(parseISO(c.checkin_date), 'MMM d')}: ${c.completed ? 'Completed' : 'Missed'}${c.measured_value !== null ? ` (${c.measured_value})` : ''}`}
                      className={`w-7 h-7 rounded flex items-center justify-center text-xs ${
                        c.completed 
                          ? 'bg-primary/20 text-primary' 
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {c.completed ? (
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      ) : (
                        <XCircle className="h-3.5 w-3.5" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {checkins.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-2">
                No check-ins recorded yet. Start tracking your progress!
              </p>
            )}
          </CardContent>
        </Card>

        {/* C. Correlations Panel */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">What Your Data Suggests</CardTitle>
          </CardHeader>
          <CardContent>
            <ChallengeCorrelationsPanel
              userId={challenge.user_id}
              category={challenge.category}
              metricSource={metricSource}
              metricKey={metricKey}
            />
          </CardContent>
        </Card>

        {/* D. Therappi Reflection */}
        <Card className="bg-primary/5 border-primary/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              Therappi Reflection
            </CardTitle>
          </CardHeader>
          <CardContent>
            {reflectionLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-4/5" />
                <Skeleton className="h-3 w-3/5" />
              </div>
            ) : therappiReflection ? (
              <p className="text-xs text-muted-foreground whitespace-pre-line leading-relaxed">
                {therappiReflection}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Keep logging your check-ins and Therappi will reflect back patterns as they emerge.
              </p>
            )}
          </CardContent>
        </Card>

        {/* E. Goal Alignment */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Target className="h-4 w-4 text-secondary" />
              Goal Alignment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between py-2 border-b border-border/50">
                <span className="text-muted-foreground">Metric Source</span>
                <span className="font-medium">{formatMetricSource(metricSource)}</span>
              </div>
              {metricKey && (
                <div className="flex items-center justify-between py-2 border-b border-border/50">
                  <span className="text-muted-foreground">Metric Key</span>
                  <span className="font-medium capitalize">{metricKey.replace(/_/g, ' ')}</span>
                </div>
              )}
              <div className="flex items-center justify-between py-2 border-b border-border/50">
                <span className="text-muted-foreground">Target Type</span>
                <Badge variant="outline" className="text-xs">
                  {getTargetTypeLabel(targetType)}
                </Badge>
              </div>
              {targetValue !== null && (
                <div className="flex items-center justify-between py-2 border-b border-border/50">
                  <span className="text-muted-foreground">Target Value</span>
                  <span className="font-medium">
                    {targetValue} {unit}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between py-2 border-b border-border/50">
                <span className="text-muted-foreground">Current Streak</span>
                <span className="font-medium">{streak} day{streak !== 1 ? 's' : ''}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-muted-foreground">Consistency</span>
                <span className="font-medium">
                  {challenge.duration_days > 0 
                    ? Math.round((completedDays / challenge.duration_days) * 100) 
                    : 0}%
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* F. Explore Further CTA */}
        <Card className="border-secondary/30 bg-secondary/5">
          <CardContent className="py-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <TrendingUp className="h-5 w-5 text-secondary" />
                <div>
                  <p className="text-sm font-medium">Explore deeper patterns</p>
                  <p className="text-xs text-muted-foreground">See how this challenge connects to your overall wellness data</p>
                </div>
              </div>
              <Button 
                variant="secondary" 
                size="sm"
                onClick={() => navigate('/vault?tab=correlations')}
              >
                Open The Vault
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ChallengeDetail;
